import React, { useRef, useState, useEffect } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { Fragment } from 'react';

const TruncatedTooltip = ({ children, content, position = 'top center' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [content, children]);

  const handleMouseEnter = () => {
    if (isTruncated) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => setIsHovered(false);

  const getMarginPosition = () => {
    const positionArray = position.split(' ');
    const marginPosition = positionArray[0];
    switch (marginPosition) {
      case 'top':
        return 'mb-1';
      case 'bottom':
        return 'mt-1';
      case 'left':
        return 'mr-1';
      case 'right':
        return 'ml-1';
      default:
        return 'mb-1';
    }
  };

  // Clone children to add ref for truncation detection
  const childrenWithRef = React.cloneElement(children, {
    ref: (node) => {
      textRef.current = node;
      // Call original ref if it exists
      const originalRef = children.ref;
      if (typeof originalRef === 'function') {
        originalRef(node);
      } else if (originalRef) {
        originalRef.current = node;
      }
    },
  });

  // If not truncated, just render the children without tooltip wrapper
  if (!isTruncated) {
    return childrenWithRef;
  }

  return (
    <div
      className="relative w-full min-w-0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover className="relative w-full min-w-0">
        {() => (
          <>
            <PopoverButton
              as="div"
              className="w-full min-w-0 text-left block pointer-events-none"
            >
              {childrenWithRef}
            </PopoverButton>

            <Transition
              show={isHovered}
              as={Fragment}
              enter="transition-opacity duration-300 ease-in-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-300 ease-in-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <PopoverPanel
                static
                anchor={position}
                portal
                className={`bg-gray-800 text-white text-xs rounded px-2 py-1 shadow-sm !max-w-[400px] leading-normal z-[10000] mx-v3-ms pointer-events-none break-words ${getMarginPosition()}`}
                style={{ color: '#fff' }}
              >
                {typeof content === 'string' ? (
                  <span className="leading-normal break-words" dangerouslySetInnerHTML={{ __html: content }} />
                ) : (
                  <span className="leading-normal break-words">{content}</span>
                )}
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default TruncatedTooltip;

